class ProductPage {
  constructor() {
    this.productEl = document.querySelector(".product");

    if (!this.productEl) return;

    this.productId = this.productEl.dataset.productId;
    this.productData = this.getProductData();

    this.carousel = document.querySelector(".product__carousel");
    this.dotsWrapper = document.querySelector(".product__carousel-dots");
    this.mobileSwiper = window.productSwipers?.[0] || null;

    if (!this.productData) return;

    this.initModals();
    this.initVariantLogic();
    this.syncFromURL();
    this.initCarouselDots();
  }

  /* GET PRODUCT DATA */
  getProductData() {
    const jsonEl = document.getElementById(`ProductData-${this.productId}`);
    if (!jsonEl) return null;

    try {
      const data = JSON.parse(jsonEl.textContent);

      this.variantImages = data.variantImages || {};
      this.formattedPrices = data.formattedPrices || {};

      return data.product;
    } catch (e) {
      console.error("Product JSON parse error:", e);
      return null;
    }
  }

  rebuildDots() {
    if (!this.dotsWrapper) return;

    const items = this.carousel.querySelectorAll(".product__carousel-item");

    this.dotsWrapper.innerHTML = "";

    items.forEach((item, index) => {
      const dot = document.createElement("button");

      dot.className = "product__dot";
      if (index === 0) dot.classList.add("is-active");

      dot.dataset.index = index;
      dot.setAttribute("aria-label", `Go to image ${index + 1}`);

      this.dotsWrapper.appendChild(dot);
    });

    this.initCarouselDots();
  }

  // Variant image update logic
  updateGallery(variant) {
    const images = this.variantImages[variant.id];

    if (!images || !images.length) {
      return;
    }

    // clear gallery
    this.carousel.innerHTML = "";

    images.forEach((src, index) => {
      const item = document.createElement("div");
      item.className = "product__carousel-item";
      item.id = `img-${index}`;

      item.innerHTML = `
      <div class="aspectholder">
        <img src="${src}" srcset="${src}" loading="${index === 0 ? "eager" : "lazy"}">
      </div>
    `;

      this.carousel.appendChild(item);
    });

    this.rebuildDots();

    this.updateMobileGallery(images);
  }

  updateMobileGallery(images) {
    if (!this.mobileSwiper) return;

    const swiper = this.mobileSwiper;

    swiper.removeAllSlides();

    const slides = images.map((src, index) => {
      return `
      <div class="swiper-slide">
        <div class="aspectholder">
          <img src="${src}" srcset="${src}" loading="${index === 0 ? "eager" : "lazy"}">
        </div>
      </div>
    `;
    });

    swiper.appendSlide(slides);

    swiper.update();
    swiper.slideTo(0, 300);
  }

  /* MODALS */
  initModals() {
    const modalTriggers = document.querySelectorAll(".product__modal-trigger");
    const modals = document.querySelectorAll(".product__modal");
    const overlay = document.querySelector(".product__modal-overlay");
    const closeButtons = document.querySelectorAll("[data-close-modal]");

    if (!modalTriggers.length) return;

    const closeAll = () => {
      modals.forEach((m) => m.classList.remove("is-open"));
      overlay?.classList.remove("is-open");
      document.body.classList.remove("modal-open");
    };

    modalTriggers.forEach((btn) => {
      btn.addEventListener("click", () => {
        const target = document.getElementById(btn.dataset.modalTarget);
        if (!target) return;

        closeAll();

        target.classList.add("is-open");
        overlay?.classList.add("is-open");
        document.body.classList.add("modal-open");
      });
    });

    closeButtons.forEach((btn) => {
      btn.addEventListener("click", closeAll);
    });

    overlay?.addEventListener("click", closeAll);

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closeAll();
      }
    });
  }

  /* VARIANTS */
  initVariantLogic() {
    this.variantGroups = document.querySelectorAll(".product__variant-pills");
    this.variantInput = document.getElementById("product-select");
    this.cartBtn = document.getElementById("product-btn");
    this.priceSale = document.getElementById("product__price-sale");
    this.priceOg = document.getElementById("product__price-og");

    if (this.variantGroups.length) {
      this.variantGroups.forEach((group) => {
        group.addEventListener("click", (e) => {
          const btn = e.target.closest("button");
          if (!btn) return;

          this.handleVariantClick(btn);
        });
      });
    }

    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");

    let initialVariant = null;

    // 1. If URL contains variant
    if (variantId) {
      initialVariant = this.productData.variants.find((v) => v.id == variantId);
    }

    // 2. Otherwise select first available variant
    if (!initialVariant) {
      initialVariant = this.productData.variants.find((v) => v.available);
    }

    // 3. Absolute fallback (all variants sold out)
    if (!initialVariant) {
      initialVariant = this.productData.variants[0];
    }

    if (initialVariant) {
      this.setActiveButtonsFromVariant(initialVariant);
      this.updateVariantUI(initialVariant);
      this.updateVariantAvailability();
      this.updateGallery(initialVariant);
    }
  }

  setActiveButtonsFromVariant(variant) {
    this.productData.options.forEach((_, index) => {
      const position = index + 1;
      const value = variant[`option${position}`];

      const group = document.querySelector(
        `.product__variant-pills[data-option-position="${position}"]`,
      );

      if (!group) return;

      group.querySelectorAll("button").forEach((btn) => {
        btn.classList.toggle("active", btn.dataset.value === value);
      });
    });
  }

  handleVariantClick(clickedBtn) {
    const parentGroup = clickedBtn.closest(".product__variant-pills");

    parentGroup
      .querySelectorAll("button")
      .forEach((btn) => btn.classList.remove("active"));

    clickedBtn.classList.add("active");
    this.updateFromSelected();
  }

  updateFromSelected() {
    const selectedOptions = [];

    this.productData.options.forEach((optionName, index) => {
      const group = document.querySelector(
        `.product__variant-pills[data-option-position="${index + 1}"]`,
      );

      const active = group?.querySelector(".active");
      selectedOptions.push(active?.dataset.value || null);
    });

    if (selectedOptions.some((opt) => !opt)) {
      if (this.cartBtn) {
        this.cartBtn.classList.remove("available");
      }
      return;
    }

    const matchedVariant = this.productData.variants.find((variant) => {
      return selectedOptions.every((opt, index) => {
        return variant[`option${index + 1}`] === opt;
      });
    });

    if (matchedVariant) {
      this.updateVariantUI(matchedVariant);
    }
  }

  scrollGalleryTop() {
    const firstImage = document.getElementById("img-0");
    if (!firstImage) return;

    firstImage.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  updateVariantUI(variant) {
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.cartBtn) {
      if (this.cartBtn) {
        this.cartBtn.classList.toggle("available", variant.available);
      }
    }

    if (this.priceOg) {
      this.priceOg.innerHTML = this.formattedPrices[variant.id].price;
    }

    if (this.priceSale) {
      if (variant.compare_at_price > variant.price) {
        this.priceSale.style.display = "block";
        this.priceSale.innerHTML = this.formattedPrices[variant.id].compare;
      } else {
        this.priceSale.style.display = "none";
      }
    }

    this.productData.options.forEach((_, index) => {
      const position = index + 1;

      const label = document.querySelector(
        `.active-option-label[data-option-position="${position}"]`,
      );

      if (label) {
        label.textContent = variant[`option${position}`];
      }
    });

    this.updateURL(variant.id);
    this.updateVariantAvailability();
    this.updateGallery(variant);
    if (window.innerWidth > 1024) {
      this.scrollGalleryTop();
    }
  }

  updateVariantAvailability() {
    this.productData.options.forEach((optionName, index) => {
      const position = index + 1;

      const group = document.querySelector(
        `.product__variant-pills[data-option-position="${position}"]`,
      );

      if (!group) return;

      const selectedOptions = [];

      this.productData.options.forEach((_, i) => {
        const active = document.querySelector(
          `.product__variant-pills[data-option-position="${i + 1}"] .active`,
        );
        selectedOptions.push(active?.dataset.value || null);
      });

      group.querySelectorAll("button").forEach((btn) => {
        const testOptions = [...selectedOptions];
        testOptions[index] = btn.dataset.value;

        const matchingVariant = this.productData.variants.find((variant) => {
          return testOptions.every((opt, i) => {
            if (!opt) return true;
            return variant[`option${i + 1}`] === opt;
          });
        });

        const isAvailable = matchingVariant?.available;

        btn.classList.toggle("is-out-of-stock", !isAvailable);
      });
    });
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url);
  }

  /* CAROUSEL DOTS */
  initCarouselDots() {
    const dots = document.querySelectorAll(".product__dot");
    const items = document.querySelectorAll(".product__carousel-item");

    if (!dots.length) return;

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const index = dot.dataset.index;
        const target = document.getElementById(`img-${index}`);
        if (!target) return;

        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = entry.target.id.replace("img-", "");

            dots.forEach((dot) => dot.classList.remove("is-active"));

            const activeDot = document.querySelector(
              `.product__dot[data-index="${index}"]`,
            );

            if (activeDot) activeDot.classList.add("is-active");
          }
        });
      },
      {
        root: null,
        threshold: 0.6,
      },
    );

    items.forEach((item) => observer.observe(item));

    const dotsWrapper = document.querySelector(".product__carousel-dots");
    const lastItem = items[items.length - 1];

    if (!dotsWrapper || !lastItem) return;

    const handleScroll = () => {
      const rect = lastItem.getBoundingClientRect();

      if (rect.bottom <= 0) {
        dotsWrapper.classList.add("is-hidden");
      } else {
        dotsWrapper.classList.remove("is-hidden");
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    handleScroll();
  }

  syncFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");
    if (!variantId) return;

    const variant = this.productData.variants.find((v) => v.id == variantId);

    if (!variant) return;

    this.productData.options.forEach((_, index) => {
      const value = variant[`option${index + 1}`];

      const group = document.querySelector(
        `.product__variant-pills[data-option-position="${index + 1}"]`,
      );

      if (!group) return;

      group.querySelectorAll("button").forEach((btn) => {
        btn.classList.toggle("active", btn.dataset.value === value);
      });
    });

    this.updateVariantUI(variant);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new ProductPage();
});
